import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Table, type TableColumn } from '../../components/ui/Table.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useCanClient } from '../../hooks/useCanClient.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import { fmtFechaDisplay } from '../../lib/dates.js';
import { capitalizarDisplay } from '../../lib/format.js';
import { PlataformaLogo } from './PlataformaLogo.js';
import type { ClienteDetalleResponse, Contacto, LogCambio, Plataforma } from './types.js';

const ESTATUS_TONE: Record<string, BadgeTone> = {
  activo: 'success',
  pausado: 'orange',
  pendiente: 'info',
  fuera_del_servicio: 'neutral',
};

const ESTATUS_LABEL: Record<string, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  pendiente: 'Pendiente',
  fuera_del_servicio: 'Fuera del servicio',
};

const ESTATUS_OPCIONES = ['activo', 'pausado', 'pendiente', 'fuera_del_servicio'] as const;

// Prompt BK-04 — mismo criterio visual que ESTATUS_TONE: A urgente/alta, B
// media, C baja (rojo/naranja/gris, no hay un tono "amarillo" en Badge).
const PRIORIDAD_TONE: Record<string, BadgeTone> = { A: 'danger', B: 'orange', C: 'neutral' };

type DocPropuestaKey = 'doc_plan_trabajo' | 'doc_carta_intencion' | 'doc_propuesta' | 'doc_contrato' | 'doc_sla' | 'doc_nda';

const PROPUESTA_DOCS: { key: DocPropuestaKey; label: string }[] = [
  { key: 'doc_plan_trabajo', label: 'Plan de trabajo' },
  { key: 'doc_carta_intencion', label: 'Carta de intención' },
  { key: 'doc_propuesta', label: 'Propuesta' },
  { key: 'doc_contrato', label: 'Contrato' },
  { key: 'doc_sla', label: 'SLA' },
  { key: 'doc_nda', label: 'NDA' },
];

type Tab = 'datos' | 'contactos' | 'plataformas' | 'historial';

const TABS: { key: Tab; label: string }[] = [
  { key: 'datos', label: 'Datos' },
  { key: 'contactos', label: 'Contactos' },
  { key: 'plataformas', label: 'Plataformas' },
  { key: 'historial', label: 'Historial' },
];

const CONTACTO_COLUMNS: TableColumn<Contacto>[] = [
  { key: 'nombre', header: 'Nombre', render: c => c.nombre },
  { key: 'correo', header: 'Correo', render: c => c.correo ?? '—' },
  { key: 'telefono', header: 'Teléfono', render: c => c.telefono ?? '—' },
  { key: 'rol', header: 'Rol', render: c => c.rol ?? '—' },
];

// Prompt BK-04 — solo los secundarios muestran la columna "Copiar en
// comunicaciones" (un primario siempre se copia, ver obtenerCorreosClientesActivos).
const CONTACTO_SECUNDARIO_COLUMNS: TableColumn<Contacto>[] = [
  ...CONTACTO_COLUMNS,
  { key: 'copiar', header: 'Copiar en comunicaciones', render: c => (c.copiar_en_comunicaciones ? 'Sí' : 'No') },
];

const PLATAFORMA_COLUMNS: TableColumn<Plataforma>[] = [
  {
    key: 'plataforma',
    header: 'Plataforma',
    render: p => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PlataformaLogo nombre={p.plataforma} />
        {p.plataforma}
      </div>
    ),
  },
  { key: 'estado', header: 'Estado', render: p => capitalizarDisplay(p.estado_permisos) },
  { key: 'responsable', header: 'Responsable del reporte', render: p => p.responsable ?? '—' },
  { key: 'frecuencia', header: 'Recurrencia', render: p => (p.frecuencia_reporte ? capitalizarDisplay(p.frecuencia_reporte) : '—') },
];

function DetField({ label, value }: { label: string; value: string }) {
  return (
    <div className="det-field">
      <label>{label}</label>
      <p>{value}</p>
    </div>
  );
}

export function ClienteDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, patch } = useApi();
  const queryClient = useQueryClient();
  const { canEditClient, canChangeStatus, canViewContacts, puedeReactivarFueraDeServicio } = useCanClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [tab, setTab] = useState<Tab>('datos');
  const [estatusPendiente, setEstatusPendiente] = useState<string | null>(null);

  const { data: cliente, isLoading } = useQuery({
    queryKey: ['cliente', id],
    queryFn: () => get<ClienteDetalleResponse>(`/clientes/${id}`),
    enabled: Boolean(id),
  });

  const { data: log } = useQuery({
    queryKey: ['cliente-log', id],
    queryFn: () => get<LogCambio[]>(`/clientes/${id}/log`),
    enabled: Boolean(id) && tab === 'historial',
  });

  const cambiarEstatusMutation = useMutation({
    mutationFn: (nuevoEstatus: string) => patch(`/clientes/${id}/estatus`, { estatus: nuevoEstatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cliente', id] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      showToast('success', 'Estatus actualizado correctamente.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible cambiar el estatus.');
    },
  });

  function handleEstatusChange(nuevo: string) {
    if (!cliente || nuevo === cliente.estatus) return;
    if (nuevo === 'fuera_del_servicio') {
      setEstatusPendiente(nuevo);
      return;
    }
    cambiarEstatusMutation.mutate(nuevo);
  }

  function confirmarEstatusPendiente() {
    if (estatusPendiente) cambiarEstatusMutation.mutate(estatusPendiente);
    setEstatusPendiente(null);
  }

  if (isLoading || !cliente) {
    return <p style={{ color: 'var(--muted)' }}>Cargando…</p>;
  }

  // digital no tiene canViewContacts (SRS-01 §5.2) — ni la pestaña ni la
  // sección deben mostrarse; el backend además ya omite cliente.contactos.
  const tabsVisibles = TABS.filter(t => t.key !== 'contactos' || canViewContacts);

  const iniciales = cliente.nombre_comercial.slice(0, 2).toUpperCase();
  const esFueraDeServicio = cliente.estatus === 'fuera_del_servicio';

  return (
    <div>
      <Link to="/clientes" className="back-btn">
        ← Volver a clientes
      </Link>

      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 13, padding: '22px 24px', marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="avatar-circle" style={{ width: 52, height: 52, fontSize: 16 }}>
              {iniciales}
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)' }}>{cliente.nombre_comercial}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{cliente.razon_social}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 9, flexWrap: 'wrap' }}>
                <Badge tone={ESTATUS_TONE[cliente.estatus] ?? 'neutral'}>{ESTATUS_LABEL[cliente.estatus] ?? cliente.estatus}</Badge>
                {cliente.level && <Badge tone="info">{cliente.level}</Badge>}
                {cliente.prioridad && <Badge tone={PRIORIDAD_TONE[cliente.prioridad] ?? 'neutral'}>Prioridad {cliente.prioridad}</Badge>}
                {cliente.fecha_salida && (
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>Fecha de salida: {fmtFechaDisplay(cliente.fecha_salida)}</span>
                )}
                {canChangeStatus && (
                  <select
                    className="filter-input"
                    style={{ marginLeft: 4 }}
                    value={cliente.estatus}
                    disabled={(esFueraDeServicio && !puedeReactivarFueraDeServicio) || cambiarEstatusMutation.isPending}
                    title={
                      esFueraDeServicio && !puedeReactivarFueraDeServicio
                        ? 'Cliente fuera de servicio — para reactivar, solicite la reactivación al administrador global'
                        : 'Cambiar estatus'
                    }
                    onChange={e => handleEstatusChange(e.target.value)}
                  >
                    {ESTATUS_OPCIONES.map(opcion => (
                      <option key={opcion} value={opcion}>
                        {ESTATUS_LABEL[opcion]}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
          {canEditClient && (
            <Button variant="secondary" size="sm" onClick={() => navigate(`/clientes/${id}/editar`)}>
              Editar
            </Button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
        {tabsVisibles.map(t => (
          <button key={t.key} className={`det-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'datos' && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 13, padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <DetField label="KAM(s) Asignado(s)" value={cliente.kams.length ? cliente.kams.map(k => k.nombre).join(', ') : '—'} />
          <DetField label="Tipo de Entrada" value={cliente.tipo_entrada} />
          <DetField label="Territorio" value={cliente.territorio ?? '—'} />
          <DetField label="País de Operación" value={cliente.pais_operacion ?? '—'} />
          <DetField label="Moneda de Facturación" value={cliente.billing_currency} />
          <DetField label="Giro" value={cliente.giro ?? '—'} />
          <DetField label="Idioma" value={cliente.idioma} />
          <DetField label="Fecha Inicio de Servicio" value={fmtFechaDisplay(cliente.fecha_inicio_servicio)} />
          <DetField label="Comisiones de Ads" value={cliente.comisiones_ads ? `Sí — desde ${fmtFechaDisplay(cliente.fecha_inicio_comisiones_ads)}` : 'No'} />
          <DetField label="Terminación de Contrato" value={fmtFechaDisplay(cliente.terminacion_contrato)} />
          <div className="det-field" style={{ gridColumn: '1 / -1' }}>
            <label>Propuesta</label>
            <p>
              {PROPUESTA_DOCS.filter(d => cliente[d.key]).map(d => d.label).join(', ') || 'Ningún documento marcado'}
              {' '}({PROPUESTA_DOCS.filter(d => cliente[d.key]).length}/{PROPUESTA_DOCS.length})
            </p>
          </div>
        </div>
      )}

      {tab === 'contactos' && canViewContacts && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--heading)', marginBottom: 8 }}>Contactos primarios</div>
            <Table
              columns={CONTACTO_COLUMNS}
              data={(cliente.contactos ?? []).filter(c => c.es_principal)}
              rowKey={c => c.id}
              emptyMessage="Sin contactos primarios registrados"
            />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--heading)', marginBottom: 8 }}>Contactos secundarios</div>
            <Table
              columns={CONTACTO_SECUNDARIO_COLUMNS}
              data={(cliente.contactos ?? []).filter(c => !c.es_principal)}
              rowKey={c => c.id}
              emptyMessage="Sin contactos secundarios registrados"
            />
          </div>
        </div>
      )}

      {tab === 'plataformas' && (
        <Table columns={PLATAFORMA_COLUMNS} data={cliente.plataformas} rowKey={p => p.id} emptyMessage="Sin plataformas registradas" />
      )}

      {tab === 'historial' && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 13, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!log?.length && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Sin movimientos registrados</p>}
          {log?.map(entry => (
            <div key={entry.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--heading)' }}>{entry.accion}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {entry.usuario_nombre} · {new Date(entry.timestamp).toLocaleString('es-MX')}
              </div>
              {/* Prompt BK-04 — CAMBIO_ESTATUS ya llega con la frase completa
                  armada en el backend ("Marcado como Pausado el DD/MM/AAAA"),
                  se muestra tal cual en vez del formato crudo "campo: a → b". */}
              {entry.accion === 'CAMBIO_ESTATUS' && entry.valor_nuevo ? (
                <div style={{ fontSize: 12.5, color: 'var(--body)', marginTop: 4 }}>{entry.valor_nuevo}</div>
              ) : (
                entry.campo_modificado && (
                  <div style={{ fontSize: 12.5, color: 'var(--body)', marginTop: 4 }}>
                    {entry.campo_modificado}: {entry.valor_anterior || '—'} → {entry.valor_nuevo || '—'}
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={estatusPendiente !== null}
        title="¿Marcar cliente fuera del servicio?"
        body="Esta acción es irreversible desde esta interfaz: una vez marcado como 'Fuera del servicio', el cliente no podrá reactivarse aquí — para volver a operar con él solicite la reactivación al administrador global."
        confirmLabel="Sí, marcar fuera del servicio"
        danger
        onConfirm={confirmarEstatusPendiente}
        onCancel={() => setEstatusPendiente(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
